import { Box, Button, Paper, Slide, Snackbar, Typography } from "@mui/material";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import useNewVersionAvailable from "../../Hooks/useNewVersionAvailable";
import { VERSION_CHECK_INTERVAL } from "../../Config/uiConfig";
import { Vocabulary } from "../../Utils/Vocabulary";

function handleRefresh(): void {
  window.location.reload();
}

/**
 * Shows a persistent toast once a newer frontend build has been deployed,
 * letting the user refresh on their own terms instead of a reload forced on
 * them that could interrupt in-progress work, such as a multi-step form.
 */
export default function NewVersionBanner() {
  const hasNewVersion = useNewVersionAvailable(VERSION_CHECK_INTERVAL);

  return (
    <Snackbar
      open={hasNewVersion}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: "up" } as never}
      sx={{ bottom: { xs: 72, sm: 24 } }}
    >
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          pl: 1.5,
          pr: 1,
          py: 1,
          maxWidth: 420,
          borderRadius: 3,
          bgcolor: "#1B2030",
          border: "1px solid rgba(255, 102, 51, 0.25)",
          boxShadow: "0 16px 40px rgba(10, 14, 25, 0.45)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 36,
            height: 36,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              bgcolor: "rgba(255, 102, 51, 0.35)",
              animation: "newVersionPulse 2.2s ease-out infinite",
              "@keyframes newVersionPulse": {
                "0%": { transform: "scale(0.9)", opacity: 0.7 },
                "70%": { transform: "scale(1.6)", opacity: 0 },
                "100%": { opacity: 0 },
              },
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              bgcolor: "rgba(255, 102, 51, 0.16)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AutorenewRoundedIcon sx={{ color: "#FF6633", fontSize: 20 }} />
          </Box>
        </Box>

        <Typography
          sx={{
            flexGrow: 1,
            color: "#E7E9F3",
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.4,
          }}
        >
          {Vocabulary.newVersionAvailable}
        </Typography>

        <Button
          onClick={handleRefresh}
          disableElevation
          sx={{
            flexShrink: 0,
            whiteSpace: "nowrap",
            bgcolor: "#FF6633",
            color: "#fff",
            fontWeight: 600,
            fontSize: 13,
            textTransform: "none",
            borderRadius: 2,
            px: 1.75,
            "&:hover": { bgcolor: "#E6572A" },
          }}
        >
          {Vocabulary.reloadPage}
        </Button>
      </Paper>
    </Snackbar>
  );
}
